import type {
  CollectionThreadResolution,
  CollectionThreadType,
} from '@erp/rpc';

/**
 * Single source of truth for collection-thread type and resolution labels.
 *
 * These maps were previously copy-pasted across several components (ThreadRow,
 * CollectionThreadDetailView, the activity timeline, the inbox body, and the
 * threads table). Centralizing them here means adding a new
 * thread type or resolution is a one-line change that ripples everywhere, and
 * the `Record<…>` types make the maps exhaustive — a missing key is a compile
 * error.
 */

/** Full human label per thread type. */
export const TYPE_LABELS: Record<CollectionThreadType, string> = {
  short_pay: 'Short pay',
  over_payment: 'Over-payment',
  deduction: 'Deduction',
  other: 'Other',
};

/** Compact label per thread type, for dense surfaces like the inbox rail. */
export const TYPE_LABELS_SHORT: Record<CollectionThreadType, string> = {
  short_pay: 'Short',
  over_payment: 'Over-pay',
  deduction: 'Deduction',
  other: 'Other',
};

/**
 * Resolution options in display order. `RESOLUTION_LABELS` is derived from this
 * so the label map and the ordered dropdown never drift.
 */
export const RESOLUTION_OPTIONS: {
  value: CollectionThreadResolution;
  label: string;
}[] = [
  { value: 'paid_in_full', label: 'Paid in full' },
  { value: 'short_pay_accepted', label: 'Short pay accepted' },
  { value: 'written_off', label: 'Written off' },
  { value: 'customer_no_response', label: 'Customer no response' },
  { value: 'no_longer_relevant', label: 'No longer relevant' },
  { value: 'opened_by_mistake', label: 'Opened by mistake' },
  // Over-payment outcomes
  { value: 'refunded', label: 'Refunded' },
  { value: 'applied_to_account', label: 'Applied to account' },
  { value: 'credit_issued', label: 'Credit issued' },
  // Deduction outcomes
  { value: 'deduction_approved', label: 'Deduction approved' },
  { value: 'deduction_charged_back', label: 'Charged back' },
  { value: 'other', label: 'Other' },
];

export const RESOLUTION_LABELS: Record<CollectionThreadResolution, string> =
  Object.fromEntries(
    RESOLUTION_OPTIONS.map((o) => [o.value, o.label]),
  ) as Record<CollectionThreadResolution, string>;

/** Thread-type options in display order, for create/filter dropdowns. */
export const TYPE_OPTIONS: {
  value: CollectionThreadType;
  label: string;
}[] = (Object.keys(TYPE_LABELS) as CollectionThreadType[]).map((value) => ({
  value,
  label: TYPE_LABELS[value],
}));

export function isValidThreadType(
  value: string,
): value is CollectionThreadType {
  return Object.prototype.hasOwnProperty.call(TYPE_LABELS, value);
}

export function isValidResolution(
  value: string,
): value is CollectionThreadResolution {
  return Object.prototype.hasOwnProperty.call(RESOLUTION_LABELS, value);
}
